<template>
  <div class="page">
    <div class="orders">
      <div class="orders__toolbar">
        <el-input
          v-model="keyword"
          clearable
          class="orders__search"
          :placeholder="$t('orders.searchPh')"
          @keyup.enter.native="onSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="onSearch" />
        </el-input>
        <el-button plain :loading="loading" @click="loadList">{{ $t('orders.refresh') }}</el-button>
      </div>

      <div v-if="!useBackend()" class="orders__empty">{{ $t('orders.needBackend') }}</div>
      <div v-else-if="!isLoggedIn" class="orders__empty">
        {{ $t('orders.needLogin') }}
      </div>
      <div v-else class="orders__panel">
        <div v-loading="loading" class="orders__body">
          <div v-if="list.length > 0" class="orders__list">
            <article
              v-for="row in list"
              :key="row.out_trade_no"
              class="order-item"
            >
              <div class="order-item__cover">
                <img
                  v-if="coverUrl(row.game_cover)"
                  class="order-item__img"
                  :src="coverUrl(row.game_cover)"
                  :alt="row.game_name"
                  loading="lazy"
                >
                <div v-else class="order-item__placeholder">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </div>
              <div class="order-item__main">
                <div class="order-item__head">
                  <div class="order-item__titleWrap">
                    <h3 class="order-item__name" :title="row.game_name">{{ row.game_name || '—' }}</h3>
                    <span class="order-item__price">{{ formatPrice(row.amount) }}</span>
                  </div>
                  <el-tag class="order-item__status" :type="statusType(row.status)" size="small">
                    {{ statusText(row.status) }}
                  </el-tag>
                </div>
                <div class="order-item__info">
                  <div class="order-item__field">
                    <span class="order-item__label">{{ $t('orders.colOrderNo') }}</span>
                    <code class="order-item__value">{{ row.out_trade_no }}</code>
                  </div>
                  <div class="order-item__field">
                    <span class="order-item__label">{{ $t('orders.colPlan') }}</span>
                    <strong class="order-item__value">{{ planLabel(row.senseyun_plan) }}</strong>
                  </div>
                  <div class="order-item__field">
                    <span class="order-item__label">{{ $t('orders.colPayChannel') }}</span>
                    <strong class="order-item__value">{{ payChannelLabel(row.pay_channel) }}</strong>
                  </div>
                  <div class="order-item__field">
                    <span class="order-item__label">{{ $t('orders.colCreated') }}</span>
                    <strong class="order-item__value">{{ formatTime(row.created_at) }}</strong>
                  </div>
                </div>
                <div class="order-item__footer">
                  <div class="order-item__actions">
                    <el-button size="small" type="primary" plain @click="openDetail(row)">
                      {{ $t('orders.viewDetail') }}
                    </el-button>
                    <el-button
                      v-if="row.has_card_secret"
                      size="small"
                      type="primary"
                      @click="showCardSecret(row)"
                    >
                      {{ $t('orders.viewCard') }}
                    </el-button>
                    <el-button
                      v-if="canRebind(row)"
                      size="small"
                      @click="openRebind(row)"
                    >
                      {{ $t('orders.rebind') }}
                    </el-button>
                    <el-button
                      v-if="canLock(row)"
                      size="small"
                      type="danger"
                      plain
                      :loading="lockLoadingNo === row.out_trade_no"
                      @click="submitLock(row)"
                    >
                      {{ $t('orders.lock') }}
                    </el-button>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <el-empty
            v-if="loaded && !loading && list.length === 0"
            class="orders__emptyState"
            :description="$t('orders.empty')"
          >
            <el-button type="primary" @click="$router.push('/games')">{{ $t('orders.goShop') }}</el-button>
          </el-empty>
        </div>

        <div v-if="total > 0" class="orders__pager">
          <el-pagination
            :current-page.sync="page"
            :page-size.sync="pageSize"
            :page-sizes="[10, 20, 50]"
            layout="total, sizes, prev, pager, next"
            :total="total"
            @current-change="loadList"
            @size-change="onPageSizeChange"
          />
        </div>
      </div>
    </div>

    <el-dialog
      :title="$t('orders.detailTitle')"
      :visible.sync="detailVisible"
      width="480px"
      top="0"
      append-to-body
      custom-class="zj-game-purchase-dialog"
    >
      <p class="gp__desc">{{ $t('orders.detailSub') }}</p>
      <div v-if="detailRefreshing && !detailOrder" class="gp__state">{{ $t('gamesPurchase.polling') }}</div>
      <template v-else-if="detailOrder">
        <div class="gp__summary">
          <div>
            <div class="gp__summaryLabel">{{ $t('orders.colGame') }}</div>
            <div class="gp__name">{{ detailOrder.game_name || '—' }}</div>
          </div>
          <div class="gp__priceBlock">
            <span>{{ $t('orders.colAmount') }}</span>
            <strong>{{ formatPrice(detailOrder.amount) }}</strong>
          </div>
        </div>
        <div class="gp__detail">
          <div class="gp__row">
            <span>{{ $t('orders.colOrderNo') }}</span>
            <code>{{ detailOrder.out_trade_no }}</code>
          </div>
          <div class="gp__row">
            <span>{{ $t('orders.colStatus') }}</span>
            <el-tag :type="statusType(detailOrder.status)" size="small">{{ statusText(detailOrder.status) }}</el-tag>
          </div>
          <div class="gp__row">
            <span>{{ $t('orders.colPlan') }}</span>
            <span>{{ planLabel(detailOrder.senseyun_plan) }}</span>
          </div>
          <div class="gp__row">
            <span>{{ $t('orders.colPayChannel') }}</span>
            <span>{{ payChannelLabel(detailOrder.pay_channel) }}</span>
          </div>
          <div class="gp__row">
            <span>{{ $t('orders.colCreated') }}</span>
            <span>{{ formatTime(detailOrder.created_at) }}</span>
          </div>
          <div v-if="detailOrder.platform" class="gp__row">
            <span>{{ $t('gamesPurchase.platform') }}</span>
            <span>{{ platformLabel(detailOrder.platform) }}</span>
          </div>
          <div v-if="detailOrder.authorization_account" class="gp__row">
            <span>{{ $t('gamesPurchase.authorizationAccount') }}</span>
            <span class="gp__sense">{{ detailOrder.authorization_account }}</span>
          </div>
          <div v-if="detailOrder.senseyun_user_account" class="gp__row">
            <span>{{ $t('gamesPurchase.senseyunPhone') }}</span>
            <span>{{ detailOrder.senseyun_user_account }}</span>
          </div>
          <div v-if="detailOrder.extra_auth_type === 'zhenggu_pp'" class="gp__row">
            <span>{{ $t('orders.extraAuth') }}</span>
            <span>{{ $t('orders.extraAuthZhenggu') }}</span>
          </div>
          <div v-else-if="detailOrder.extra_auth_type === 'only_pp'" class="gp__row">
            <span>{{ $t('orders.extraAuth') }}</span>
            <span>{{ $t('orders.extraAuthOnly') }}</span>
          </div>
          <div v-if="detailOrder.senseyun_desc" class="gp__row">
            <span>{{ detailFulfillmentDescLabel }}</span>
            <span class="gp__sense">{{ detailOrder.senseyun_desc }}</span>
          </div>
          <div v-if="detailCardItems.length" class="gp__row gp__row--card">
            <span>{{ $t('gamesPurchase.cardSecret') }}</span>
            <div
              v-for="(item, idx) in detailCardItems"
              :key="`${item.label}-${idx}`"
              class="gp__cardItem"
            >
              <div v-if="item.label" class="gp__cardLabel">{{ item.label }}</div>
              <code class="gp__cardSecret">{{ item.code }}</code>
            </div>
          </div>
        </div>
      </template>
      <div class="gp__footerBtns">
        <el-button
          v-if="detailOrder && detailCardItems.length"
          type="primary"
          @click="copyCardSecret(detailOrder.delivered_card_secret)"
        >
          {{ $t('orders.copyCard') }}
        </el-button>
        <el-button
          v-if="canUnbindMachine(detailOrder)"
          type="warning"
          plain
          :loading="unbindMachineLoadingNo === detailOrder.out_trade_no"
          @click="submitUnbindMachine(detailOrder)"
        >
          {{ $t('orders.unbindMachine') }}
        </el-button>
        <el-button
          v-if="canRetryFulfill(detailOrder)"
          type="primary"
          plain
          :loading="detailRefreshing"
          @click="refreshDetail(false)"
        >
          {{
            detailOrder && detailOrder.status === 'license_failed'
              ? $t('orders.retryFulfill')
              : $t('gamePurchase.refresh')
          }}
        </el-button>
        <el-button plain @click="detailVisible = false">{{ $t('gamesPurchase.close') }}</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="$t('orders.rebindTitle')"
      :visible.sync="rebindVisible"
      width="480px"
      top="0"
      append-to-body
      custom-class="zj-game-purchase-dialog"
      @closed="onRebindClosed"
    >
      <p class="gp__desc">{{ $t('orders.rebindSub') }}</p>
      <div v-if="rebindOrder" class="gp__detail">
        <div class="gp__row">
          <span>{{ $t('orders.colOrderNo') }}</span>
          <code>{{ rebindOrder.out_trade_no }}</code>
        </div>
        <div class="gp__row">
          <span>{{ $t('orders.colGame') }}</span>
          <span>{{ rebindOrder.game_name || '—' }}</span>
        </div>
        <div v-if="rebindOrder.platform" class="gp__row">
          <span>{{ $t('gamesPurchase.platform') }}</span>
          <span>{{ platformLabel(rebindOrder.platform) }}</span>
        </div>
        <div v-if="activeRebindTarget === 'game'" class="gp__row">
          <span>{{ $t('orders.rebindCurrentSenseyunPhone') }}</span>
          <span class="gp__sense">{{ rebindCurrentSenseyunPhone || '—' }}</span>
        </div>
        <div
          v-if="activeRebindTarget === 'game' && senseyunRebindRemainText"
          class="gp__row orders__rebindRemain"
        >
          <span>{{ $t('orders.rebindSenseyunRemainLabel') }}</span>
          <strong class="orders__rebindRemainValue">{{ senseyunRebindRemainText }}</strong>
        </div>
        <div v-if="isExtraAuthRebindTarget(activeRebindTarget)" class="gp__row">
          <span>{{ $t('orders.rebindCurrentAuthAccount') }}</span>
          <span class="gp__sense">{{ rebindOrder.authorization_account || '—' }}</span>
        </div>
      </div>
      <el-form label-position="top" class="gp__form orders__rebindForm">
        <el-form-item
          v-if="rebindUnbindOptions.length > 1"
          :label="$t('orders.rebindUnbindTargets')"
          required
        >
          <el-radio-group v-model="rebindUnbindTarget" @change="onRebindTargetChange">
            <el-radio
              v-for="opt in rebindUnbindOptions"
              :key="opt"
              :label="opt"
            >
              {{ rebindUnbindLabel(opt) }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item
          v-if="activeRebindTarget === 'game'"
          :label="$t('orders.rebindNewSenseyunPhone')"
          required
        >
          <el-input
            v-model.trim="rebindNewSenseyunPhone"
            maxlength="191"
            clearable
            :placeholder="$t('gamesPurchase.senseyunPhonePh')"
            class="gp__full"
          />
        </el-form-item>
        <el-form-item
          v-else-if="isExtraAuthRebindTarget(activeRebindTarget)"
          :label="$t('orders.rebindNewAccount')"
          required
        >
          <el-input
            v-model.trim="rebindNewAccount"
            maxlength="191"
            clearable
            :placeholder="$t('orders.rebindNewAccountPh')"
            class="gp__full"
          />
        </el-form-item>
      </el-form>
      <p v-if="rebindQuote" class="orders__rebindFee">
        <template v-if="rebindQuote.free_unlimited">
          {{ $t('orders.rebindFeeUnlimited') }}
        </template>
        <template v-else-if="rebindQuote.requires_fee">
          <template v-if="rebindPayChannel === 'online'">
            {{ $t('orders.rebindFeeCharge', { fee: formatPrice(rebindQuote.next_fee) }) }}
          </template>
          <template v-else>
            {{ $t('orders.rebindFeeChargeBalance', { fee: formatPrice(rebindQuote.next_fee) }) }}
          </template>
        </template>
        <template v-else>
          {{ $t('orders.rebindFeeFree', { remaining: rebindQuote.free_remaining }) }}
        </template>
      </p>
      <el-form
        v-if="showRebindPayChannel"
        label-position="top"
        class="gp__form orders__rebindForm"
      >
        <el-form-item :label="$t('orders.rebindPayChannel')" required>
          <el-select v-model="rebindPayChannel" class="orders__paySelect">
            <el-option :label="$t('orders.rebindPayBalance')" value="balance" />
            <el-option :label="$t('orders.rebindPayOnline')" value="online" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="rebindPayChannel === 'online'" :label="$t('gamesPurchase.paymentSection')">
          <div class="orders__payGrid">
            <button
              type="button"
              :class="['orders__payOption', { 'is-active': rebindPayType === 'alipay' }]"
              @click="rebindPayType = 'alipay'"
            >
              <span class="orders__payMark orders__payMark--ali">支</span>
              <span>
                <strong>支付宝</strong>
                <small>ALIPAY</small>
              </span>
              <i class="el-icon-check"></i>
            </button>
            <button
              type="button"
              :class="['orders__payOption', { 'is-active': rebindPayType === 'wxpay' }]"
              @click="rebindPayType = 'wxpay'"
            >
              <span class="orders__payMark orders__payMark--wx">微</span>
              <span>
                <strong>微信支付</strong>
                <small>WECHAT PAY</small>
              </span>
              <i class="el-icon-check"></i>
            </button>
          </div>
          <p class="orders__rebindOnlineTip">{{ $t('orders.rebindOnlineTip') }}</p>
        </el-form-item>
      </el-form>
      <div class="gp__footerBtns">
        <el-button type="primary" :loading="rebindLoading" @click="submitRebind">
          {{
            showRebindPayChannel && rebindPayChannel === 'online'
              ? $t('orders.rebindPayOnline')
              : $t('orders.rebindSubmit')
          }}
        </el-button>
        <el-button plain @click="rebindVisible = false">{{ $t('gamesPurchase.close') }}</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="rebindQrVisible"
      :title="$t('recharge.scanTitle')"
      width="360px"
      top="0"
      append-to-body
      center
      custom-class="zj-game-purchase-dialog"
    >
      <div class="gp__qr">
        <img v-if="rebindQrDataUrl" class="gp__qrImg" :src="rebindQrDataUrl" alt="qrcode">
        <div class="gp__qrTip">{{ $t('recharge.scanTip') }}</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="onRebindQrDone">{{ $t('recharge.paidGoResult') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getAuthHeader, getSession, isLoggedIn as authIsLoggedIn, sessionState } from '@/utils/auth'
import { parseDeliveredCardSecrets, formatDeliveredCardSecretsText } from '@/utils/cardSecrets'

function useBackend() {
  return process.env.VUE_APP_USE_BACKEND === 'true'
}

function apiUrl(path) {
  const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return root ? `${root}${p}` : p
}

function firstCoverPath(u) {
  const s = String(u || '').trim()
  if (!s) return ''
  if (s.startsWith('[')) {
    try {
      const a = JSON.parse(s)
      if (Array.isArray(a) && a.length) return String(a[0] || '').trim()
    } catch (e) {
      return ''
    }
    return ''
  }
  return s
}

function coverUrl(u) {
  const p = firstCoverPath(u)
  if (!p) return ''
  if (/^https?:\/\//i.test(p)) return p
  const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  if (!root) return p.startsWith('/') ? p : `/${p}`
  return p.startsWith('/') ? `${root}${p}` : `${root}/${p}`
}

export default {
  name: 'OrdersPage',
  data() {
    return {
      loading: false,
      loaded: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 20,
      keyword: '',
      detailVisible: false,
      detailOrder: null,
      detailRefreshing: false,
      rebindVisible: false,
      rebindOrder: null,
      rebindNewAccount: '',
      rebindNewSenseyunPhone: '',
      rebindUnbindTarget: 'game',
      rebindQuote: null,
      rebindLoading: false,
      rebindPayChannel: 'balance',
      rebindPayType: 'wxpay',
      rebindQrVisible: false,
      rebindQrDataUrl: '',
      rebindPayOutTradeNo: '',
      lockLoadingNo: '',
      unbindMachineLoadingNo: '',
      senseyunRebindNow: Date.now(),
      senseyunRebindTicker: null
    }
  },
  computed: {
    isLoggedIn() {
      void sessionState.user
      return authIsLoggedIn()
    },
    isAgentUser() {
      void sessionState.user
      const s = getSession()
      const level = s && s.agentLevel ? String(s.agentLevel).toLowerCase() : 'normal'
      return level === 'svip' || level === 'ssvip'
    },
    rebindUnbindOptions() {
      const row = this.rebindOrder
      if (!row) return []
      if (Array.isArray(row.rebind_options) && row.rebind_options.length) {
        return row.rebind_options
      }
      const opts = []
      if (
        row.status === 'paid' &&
        this.isExtraAuthRebindTarget(row.extra_auth_type) &&
        row.has_card_secret
      ) {
        opts.push(row.extra_auth_type)
      }
      return opts
    },
    activeRebindTarget() {
      const opts = this.rebindUnbindOptions
      if (!opts.length) return ''
      if (opts.includes(this.rebindUnbindTarget)) return this.rebindUnbindTarget
      return opts[0]
    },
    rebindCurrentSenseyunPhone() {
      const row = this.rebindOrder
      if (!row) return ''
      return String(row.senseyun_user_account || '').trim()
    },
    detailFulfillmentDescLabel() {
      const t = String((this.detailOrder && this.detailOrder.fulfillment_type) || '').toLowerCase()
      if (t === 'zhengjia_auth' || t === 'card_key') {
        return this.$t('gamesPurchase.fulfillmentDesc')
      }
      return this.$t('gamesPurchase.senseDesc')
    },
    detailCardItems() {
      return parseDeliveredCardSecrets(
        this.detailOrder && this.detailOrder.delivered_card_secret
      )
    },
    senseyunRebindDeadlineMs() {
      const row = this.rebindOrder
      if (!row || this.activeRebindTarget !== 'game') return 0
      const raw = row.senseyun_rebind_deadline
      if (!raw) return 0
      const t = new Date(raw).getTime()
      return Number.isFinite(t) ? t : 0
    },
    senseyunRebindRemainMs() {
      void this.senseyunRebindNow
      const deadline = this.senseyunRebindDeadlineMs
      if (!deadline) return 0
      return Math.max(0, deadline - Date.now())
    },
    senseyunRebindRemainText() {
      const ms = this.senseyunRebindRemainMs
      if (!this.senseyunRebindDeadlineMs) return ''
      if (ms <= 0) return this.$t('orders.rebindSenseyunRemainExpired')
      const totalSec = Math.floor(ms / 1000)
      const h = Math.floor(totalSec / 3600)
      const m = Math.floor((totalSec % 3600) / 60)
      const s = totalSec % 60
      const pad = (n) => String(n).padStart(2, '0')
      return this.$t('orders.rebindSenseyunRemainValue', {
        time: `${pad(h)}:${pad(m)}:${pad(s)}`
      })
    },
    showRebindPayChannel() {
      const q = this.rebindQuote
      return !!(
        this.isExtraAuthRebindTarget(this.activeRebindTarget) &&
        q &&
        q.requires_fee &&
        Number(q.next_fee) > 0
      )
    }
  },
  mounted() {
    if (useBackend() && this.isLoggedIn) {
      this.loadList()
    }
    this.handleRebindPayReturn()
  },
  beforeDestroy() {
    this.stopSenseyunRebindTicker()
  },
  watch: {
    '$route.query': {
      handler() {
        this.handleRebindPayReturn()
      }
    }
  },
  methods: {
    useBackend,
    isExtraAuthRebindTarget(type) {
      return type === 'zhenggu_pp' || type === 'only_pp'
    },
    handleRebindPayReturn() {
      const q = (this.$route && this.$route.query) || {}
      const rebindOut = String(q.rebind_pay_out || '').trim()
      if (!rebindOut) return
      this.$message.success(this.$t('orders.rebindOnlineTip'))
      if (useBackend() && this.isLoggedIn) {
        this.loadList()
      }
      const nextQuery = { ...q }
      delete nextQuery.rebind_pay_out
      delete nextQuery.purchase_out
      this.$router.replace({ path: this.$route.path, query: nextQuery }).catch(() => {})
    },
    onSearch() {
      this.page = 1
      this.loadList()
    },
    onPageSizeChange() {
      this.page = 1
      this.loadList()
    },
    async loadList() {
      if (!useBackend() || !this.isLoggedIn) return
      this.loading = true
      try {
        const qs = new URLSearchParams({
          page: String(this.page),
          pageSize: String(this.pageSize)
        })
        const kw = String(this.keyword || '').trim()
        if (kw) qs.set('keyword', kw)
        const res = await fetch(`${apiUrl('/api/games/purchase/orders')}?${qs.toString()}`, {
          headers: { ...getAuthHeader() }
        })
        const data = await res.json().catch(() => ({}))
        if (!data.ok) {
          this.$message.error(data.message || this.$t('orders.loadFailed'))
          return
        }
        this.list = Array.isArray(data.list) ? data.list : []
        this.total = data.total != null ? Number(data.total) : 0
        this.loaded = true
      } catch (e) {
        this.$message.error(this.$t('recharge.network'))
      } finally {
        this.loading = false
      }
    },
    openDetail(row) {
      this.detailOrder = { ...row }
      this.detailVisible = true
      if (this.shouldAutoRefreshOrder(row)) {
        this.refreshDetail(true)
      }
    },
    shouldAutoRefreshOrder(row) {
      if (!row) return false
      const st = String(row.status || '')
      if (st === 'pending' || st === 'processing' || st === 'license_failed') return true
      const needExtra =
        row.extra_auth_type === 'zhenggu_pp' || row.extra_auth_type === 'only_pp'
      const isZhengjia = String(row.fulfillment_type || '').toLowerCase() === 'zhengjia_auth'
      const emptySecret = !(row.delivered_card_secret != null && String(row.delivered_card_secret).trim())
      return st === 'paid' && emptySecret && (needExtra || isZhengjia)
    },
    canRetryFulfill(row) {
      return this.shouldAutoRefreshOrder(row)
    },
    showCardSecret(row) {
      if (!row || !row.delivered_card_secret) return
      this.openDetail(row)
    },
    canRebind(row) {
      if (!row || row.status !== 'paid') return false
      if (row.rebindable) return true
      if (Array.isArray(row.rebind_options) && row.rebind_options.length > 0) return true
      return !!(
        this.isExtraAuthRebindTarget(row.extra_auth_type) &&
        row.has_card_secret &&
        row.authorization_account
      )
    },
    canLock(row) {
      if (!this.isAgentUser || !row || row.status !== 'paid') return false
      if (Array.isArray(row.lock_options)) return row.lock_options.length > 0
      if (row.lockable) return true
      if (row.fulfillment_type === 'senseyun') return true
      return !!(this.isExtraAuthRebindTarget(row.extra_auth_type) && row.has_card_secret)
    },
    canUnbindMachine(row) {
      if (!this.isAgentUser || !row || row.status !== 'paid') return false
      if (row.can_unbind_machine === true) return true
      return (
        row.extra_auth_type === 'zhenggu_pp' &&
        Boolean(row.delivered_card_secret || row.has_card_secret)
      )
    },
    lockConfirmText(row) {
      const opts = Array.isArray(row && row.lock_options) ? row.lock_options : []
      const hasGame = opts.includes('game') || (!opts.length && row && row.fulfillment_type === 'senseyun')
      const hasExtra =
        opts.includes('zhenggu_pp') ||
        opts.includes('only_pp') ||
        (!opts.length &&
          row &&
          this.isExtraAuthRebindTarget(row.extra_auth_type) &&
          row.has_card_secret)
      if (hasGame && hasExtra) return this.$t('orders.lockConfirmBoth')
      if (hasExtra) {
        if (opts.includes('only_pp') || (row && row.extra_auth_type === 'only_pp')) {
          return this.$t('orders.lockConfirmOnly')
        }
        return this.$t('orders.lockConfirmZhenggu')
      }
      return this.$t('orders.lockConfirmGame')
    },
    async submitLock(row) {
      if (!this.canLock(row) || !row.out_trade_no) return
      try {
        await this.$confirm(this.lockConfirmText(row), this.$t('orders.lockTitle'), {
          type: 'warning',
          confirmButtonText: this.$t('orders.lockSubmit'),
          cancelButtonText: this.$t('orders.lockCancel')
        })
      } catch (e) {
        return
      }
      this.lockLoadingNo = row.out_trade_no
      try {
        const res = await fetch(apiUrl('/api/games/purchase/lock'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            out_trade_no: row.out_trade_no
          })
        })
        const data = await res.json().catch(() => ({}))
        if (data.ok) {
          this.$message.success(this.$t('orders.lockOk'))
          this.loadList()
          return
        }
        if (data.message === 'AGENT_REQUIRED') {
          this.$message.warning(this.$t('orders.lockAgentRequired'))
        } else if (data.message === 'ORDER_NOT_LOCKABLE') {
          this.$message.warning(this.$t('orders.lockNotSupported'))
        } else if (data.message === 'SENSEYUN_LOCK_FAILED') {
          this.$message.error(data.detail || this.$t('orders.lockSenseyunFailed'))
        } else if (
          data.message === 'FAKA_DISABLE_FAILED' ||
          data.message === 'FAKA_API_KEY_NOT_SET' ||
          data.message === 'ONLY_DISABLE_FAILED' ||
          data.message === 'ONLY_API_KEY_NOT_SET'
        ) {
          this.$message.error(data.detail || data.message || this.$t('orders.lockZhengguFailed'))
        } else {
          this.$message.error(data.detail || data.message || this.$t('orders.lockFailed'))
        }
      } catch (e) {
        this.$message.error(this.$t('recharge.network'))
      } finally {
        this.lockLoadingNo = ''
      }
    },
    async submitUnbindMachine(row) {
      if (!this.canUnbindMachine(row) || !row.out_trade_no) return
      try {
        await this.$confirm(this.$t('orders.unbindMachineConfirm'), this.$t('orders.unbindMachineTitle'), {
          type: 'warning',
          confirmButtonText: this.$t('orders.unbindMachineSubmit'),
          cancelButtonText: this.$t('orders.unbindMachineCancel')
        })
      } catch (e) {
        return
      }
      this.unbindMachineLoadingNo = row.out_trade_no
      try {
        const res = await fetch(apiUrl('/api/games/purchase/unbind-machine'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            out_trade_no: row.out_trade_no
          })
        })
        const data = await res.json().catch(() => ({}))
        if (data.ok) {
          const remain =
            data.order && data.order.remaining_unbind_count != null
              ? Number(data.order.remaining_unbind_count)
              : null
          if (remain != null && Number.isFinite(remain)) {
            this.$message.success(this.$t('orders.unbindMachineOkRemain', { count: remain }))
          } else {
            this.$message.success(this.$t('orders.unbindMachineOk'))
          }
          return
        }
        if (data.message === 'AGENT_REQUIRED') {
          this.$message.warning(this.$t('orders.unbindMachineAgentRequired'))
        } else if (data.message === 'ORDER_NOT_UNBINDABLE') {
          this.$message.warning(this.$t('orders.unbindMachineNotSupported'))
        } else if (data.message === 'FAKA_API_KEY_NOT_SET') {
          this.$message.error(this.$t('orders.unbindMachineNotReady'))
        } else {
          this.$message.error(data.message || this.$t('orders.unbindMachineFailed'))
        }
      } catch (e) {
        this.$message.error(this.$t('recharge.network'))
      } finally {
        this.unbindMachineLoadingNo = ''
      }
    },
    defaultRebindUnbindTarget(row) {
      const opts = Array.isArray(row && row.rebind_options) ? row.rebind_options : []
      if (opts.includes('game')) return 'game'
      if (opts.length) return opts[0]
      if (row && this.isExtraAuthRebindTarget(row.extra_auth_type) && row.has_card_secret) {
        return row.extra_auth_type
      }
      return 'game'
    },
    rebindUnbindLabel(opt) {
      if (opt === 'game') return this.$t('orders.rebindUnbindGame')
      if (opt === 'zhenggu_pp') return this.$t('orders.rebindUnbindZhenggu')
      if (opt === 'only_pp') return this.$t('orders.rebindUnbindOnly')
      return opt
    },
    openRebind(row) {
      if (!this.canRebind(row)) return
      this.rebindOrder = { ...row }
      this.rebindNewAccount = ''
      this.rebindNewSenseyunPhone = ''
      this.rebindUnbindTarget = this.defaultRebindUnbindTarget(row)
      this.rebindQuote = null
      this.rebindPayChannel = 'balance'
      this.rebindPayType = 'wxpay'
      this.rebindVisible = true
      this.startSenseyunRebindTicker()
      this.loadRebindQuote()
    },
    startSenseyunRebindTicker() {
      this.stopSenseyunRebindTicker()
      this.senseyunRebindNow = Date.now()
      this.senseyunRebindTicker = setInterval(() => {
        this.senseyunRebindNow = Date.now()
        if (this.activeRebindTarget === 'game' && this.senseyunRebindDeadlineMs && this.senseyunRebindRemainMs <= 0) {
          this.stopSenseyunRebindTicker()
        }
      }, 1000)
    },
    stopSenseyunRebindTicker() {
      if (this.senseyunRebindTicker) {
        clearInterval(this.senseyunRebindTicker)
        this.senseyunRebindTicker = null
      }
    },
    async loadRebindQuote() {
      if (!this.rebindOrder || !this.rebindOrder.out_trade_no) return
      const target = this.activeRebindTarget
      if (!target) return
      try {
        const out = encodeURIComponent(this.rebindOrder.out_trade_no)
        const tgt = encodeURIComponent(target)
        const res = await fetch(
          `${apiUrl('/api/games/purchase/rebind/quote')}?out_trade_no=${out}&unbind_target=${tgt}`,
          { headers: { ...getAuthHeader() } }
        )
        const data = await res.json().catch(() => ({}))
        if (data.ok && data.quote) {
          this.rebindQuote = data.quote
          if (data.senseyun_rebind_deadline) {
            this.rebindOrder = {
              ...this.rebindOrder,
              senseyun_rebind_deadline: data.senseyun_rebind_deadline,
              senseyun_rebind_within_24h: true
            }
            this.startSenseyunRebindTicker()
          }
          if (
            !(
              data.quote.requires_fee &&
              Number(data.quote.next_fee) > 0 &&
              this.isExtraAuthRebindTarget(target)
            )
          ) {
            this.rebindPayChannel = 'balance'
          }
        } else if (data.message === 'SENSEYUN_REBIND_EXPIRED') {
          this.$message.warning(this.$t('orders.rebindSenseyunExpired'))
        }
      } catch (e) {
        /* ignore quote load failure */
      }
    },
    onRebindClosed() {
      this.stopSenseyunRebindTicker()
      this.rebindOrder = null
      this.rebindNewAccount = ''
      this.rebindNewSenseyunPhone = ''
      this.rebindUnbindTarget = 'game'
      this.rebindQuote = null
      this.rebindLoading = false
      this.rebindPayChannel = 'balance'
      this.rebindPayType = 'wxpay'
      this.rebindQrVisible = false
      this.rebindQrDataUrl = ''
      this.rebindPayOutTradeNo = ''
    },
    onRebindTargetChange() {
      this.rebindNewAccount = ''
      this.rebindNewSenseyunPhone = ''
      this.rebindPayChannel = 'balance'
      this.loadRebindQuote()
    },
    onRebindQrDone() {
      this.rebindQrVisible = false
      this.rebindVisible = false
      this.$message.success(this.$t('orders.rebindOnlineTip'))
      this.loadList()
    },
    handleRebindPayGateway(data) {
      const payType = String(data.pay_type || '')
      const payInfo = String(data.pay_info || '')
      this.rebindPayOutTradeNo = data.out_trade_no || ''
      if (payType === 'jump') {
        this.$message.info(this.$t('gamesPurchase.jumpTip'))
        window.location.href = payInfo
        return true
      }
      if (payType === 'html') {
        document.open()
        document.write(payInfo)
        document.close()
        return true
      }
      if (payType === 'qrcode') {
        this.rebindQrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(payInfo)}`
        this.rebindQrVisible = true
        return true
      }
      this.$message.info(payInfo || this.$t('recharge.payInfo'))
      return true
    },
    normalizedRebindAccount() {
      const account = String(this.rebindNewAccount || '').trim()
      if (!account || account.length > 191) return ''
      if (/[,，;；\n\r]/.test(account)) return ''
      return account
    },
    normalizedRebindSenseyunPhone() {
      let value = String(this.rebindNewSenseyunPhone || '').trim().replace(/\s+/g, '')
      if (!value || value.length > 191) return ''
      if (value.includes('@')) {
        const email = value.toLowerCase()
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : ''
      }
      if (value.startsWith('+86')) value = value.slice(3)
      else if (/^86\d{11}$/.test(value)) value = value.slice(2)
      const digits = value.replace(/\D/g, '')
      return /^1[3-9]\d{9}$/.test(digits) ? digits : ''
    },
    validateRebindForm() {
      const target = this.activeRebindTarget
      if (!target) {
        this.$message.warning(this.$t('orders.rebindUnbindRequired'))
        return null
      }
      if (target === 'game') {
        const next = this.normalizedRebindSenseyunPhone()
        if (!next) {
          this.$message.warning(this.$t('gamesPurchase.phoneInvalid'))
          return null
        }
        const current = this.rebindCurrentSenseyunPhone
        if (current && next === current) {
          this.$message.warning(this.$t('orders.rebindSamePhone'))
          return null
        }
        return { target, senseyunPhone: next }
      }
      const next = this.normalizedRebindAccount()
      if (!next) {
        this.$message.warning(this.$t('orders.rebindNewAccountInvalid'))
        return null
      }
      const current = String(this.rebindOrder && this.rebindOrder.authorization_account || '').trim()
      if (current && next === current) {
        this.$message.warning(this.$t('orders.rebindSameAccount'))
        return null
      }
      return { target, authorizationAccount: next }
    },
    async submitRebind() {
      if (!this.rebindOrder || !this.rebindOrder.out_trade_no) return
      const payload = this.validateRebindForm()
      if (!payload) return
      this.rebindLoading = true
      try {
        const useOnline =
          this.showRebindPayChannel && this.rebindPayChannel === 'online'
        const body = {
          out_trade_no: this.rebindOrder.out_trade_no,
          unbind_target: payload.target,
          pay_channel: useOnline ? 'online' : 'balance'
        }
        if (payload.target === 'game') {
          body.senseyunPhone = payload.senseyunPhone
        } else {
          body.authorizationAccount = payload.authorizationAccount
        }
        if (useOnline) {
          body.type = this.rebindPayType
          body.method = 'web'
        }
        const res = await fetch(apiUrl('/api/games/purchase/rebind'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify(body)
        })
        const data = await res.json().catch(() => ({}))
        if (data.ok && data.pay_required) {
          this.handleRebindPayGateway(data)
          return
        }
        if (data.ok) {
          const outTradeNo = this.rebindOrder.out_trade_no
          const target = payload.target
          this.$message.success(this.$t('orders.rebindOk'))
          this.rebindVisible = false
          await this.loadList()
          if (this.detailVisible && this.detailOrder && this.detailOrder.out_trade_no === outTradeNo) {
            const patch = { ...this.detailOrder }
            if (target === 'game' && data.order && data.order.senseyun_user_account) {
              patch.senseyun_user_account = data.order.senseyun_user_account
            }
            if (this.isExtraAuthRebindTarget(target)) {
              patch.authorization_account = data.order && data.order.authorization_account
                ? data.order.authorization_account
                : payload.authorizationAccount
              if (data.order && data.order.delivered_card_secret) {
                patch.delivered_card_secret = data.order.delivered_card_secret
                patch.has_card_secret = true
              }
            }
            this.detailOrder = patch
          }
          return
        }
        if (data.message === 'ORDER_NOT_REBINDABLE') {
          this.$message.warning(this.$t('orders.rebindNotSupported'))
          return
        }
        if (data.message === 'SENSEYUN_REBIND_EXPIRED') {
          this.$message.warning(this.$t('orders.rebindSenseyunExpired'))
          return
        }
        if (data.message === 'FAKA_UNBIND_EXHAUSTED') {
          this.$message.warning(data.detail || this.$t('orders.rebindUnbindExhausted'))
          return
        }
        if (data.message === 'SENSEYUN_LOCK_FAILED' || data.message === 'SENSEYUN_UNLOCK_FAILED') {
          this.$message.error(data.detail || this.$t('orders.rebindSenseyunLockFailed'))
          return
        }
        if (data.message === 'FAKA_UNBIND_FAILED' || data.message === 'FAKA_GENERATE_FAILED') {
          this.$message.error(data.detail || data.message || this.$t('orders.rebindFailed'))
          return
        }
        if (data.message === 'REBIND_NOT_READY') {
          this.$message.warning(this.$t('orders.rebindNotReady'))
          return
        }
        if (data.message === 'INVALID_SENSEYUN_PHONE') {
          this.$message.warning(this.$t('gamesPurchase.phoneInvalid'))
          return
        }
        if (data.message === 'INSUFFICIENT_BALANCE') {
          const fee = data.quote && data.quote.next_fee != null ? data.quote.next_fee : null
          this.$message.warning(
            fee != null
              ? this.$t('orders.rebindInsufficientBalance', { fee: this.formatPrice(fee) })
              : this.$t('gamesPurchase.insufficient')
          )
          if (this.showRebindPayChannel) {
            this.rebindPayChannel = 'online'
          }
          return
        }
        if (data.message === 'INVALID_AUTHORIZATION_ACCOUNT') {
          this.$message.warning(this.$t('orders.rebindNewAccountInvalid'))
          return
        }
        if (data.message === 'REBIND_SAME_ACCOUNT') {
          this.$message.warning(this.$t('orders.rebindSameAccount'))
          return
        }
        this.$message.error(data.message || this.$t('orders.rebindFailed'))
      } catch (e) {
        this.$message.error(this.$t('recharge.network'))
      } finally {
        this.rebindLoading = false
      }
    },
    async refreshDetail(silent) {
      if (!this.detailOrder || !this.detailOrder.out_trade_no) return
      if (!silent) this.detailRefreshing = true
      const prevStatus = String(this.detailOrder.status || '')
      const prevHasSecret = !!(
        this.detailOrder.delivered_card_secret != null &&
        String(this.detailOrder.delivered_card_secret).trim()
      )
      try {
        const out = encodeURIComponent(this.detailOrder.out_trade_no)
        const res = await fetch(`${apiUrl('/api/games/purchase/status')}?out_trade_no=${out}`, {
          headers: { ...getAuthHeader() }
        })
        const data = await res.json().catch(() => ({}))
        if (!data.ok || !data.order) {
          if (!silent) this.$message.error(data.message || this.$t('recharge.queryFailed'))
          return
        }
        this.detailOrder = data.order
        const idx = this.list.findIndex((x) => x.out_trade_no === data.order.out_trade_no)
        if (idx >= 0) {
          this.$set(this.list, idx, data.order)
        }
        const nowPaid = String(data.order.status) === 'paid'
        const nowHasSecret = !!(
          data.order.delivered_card_secret != null && String(data.order.delivered_card_secret).trim()
        )
        if (!silent || prevStatus === 'license_failed') {
          if (nowPaid && nowHasSecret && !prevHasSecret) {
            this.$message.success(this.$t('orders.retryFulfillOk'))
          } else if (!silent && prevStatus === 'license_failed' && !nowPaid) {
            this.$message.warning(data.order.senseyun_desc || this.$t('orders.retryFulfillFail'))
          }
        }
      } catch (e) {
        if (!silent) this.$message.error(this.$t('recharge.network'))
      } finally {
        this.detailRefreshing = false
      }
    },
    copyCardSecret(text) {
      const val = formatDeliveredCardSecretsText(text) || String(text || '').trim()
      if (!val) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(val).then(() => {
          this.$message.success(this.$t('orders.copyOk'))
        }).catch(() => {
          this.fallbackCopy(val)
        })
        return
      }
      this.fallbackCopy(val)
    },
    fallbackCopy(text) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        this.$message.success(this.$t('orders.copyOk'))
      } catch (e) {
        this.$message.warning(this.$t('orders.copyFail'))
      }
      document.body.removeChild(ta)
    },
    coverUrl,
    formatPrice(value) {
      const amount = Number(value)
      if (Number.isNaN(amount)) return '¥0.00'
      return new Intl.NumberFormat('zh-CN', {
        style: 'currency',
        currency: 'CNY'
      }).format(amount)
    },
    formatTime(v) {
      if (!v) return '—'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    planLabel(plan) {
      const p = String(plan || '').toLowerCase()
      if (p === 'year') return this.$t('gamesPurchase.planYear')
      if (p === 'quarter') return this.$t('gamesPurchase.planQuarter')
      return this.$t('gamesPurchase.planMonth')
    },
    payChannelLabel(ch) {
      return String(ch || '').toLowerCase() === 'balance'
        ? this.$t('orders.payBalance')
        : this.$t('orders.payOnline')
    },
    platformLabel(value) {
      const map = {
        douyin: this.$t('gamesPurchase.platformDouyin'),
        kuaishou: this.$t('gamesPurchase.platformKuaishou'),
        bilibili: this.$t('gamesPurchase.platformBilibili'),
        tiktok: this.$t('gamesPurchase.platformTiktok'),
        xiaohongshu: this.$t('gamesPurchase.platformXiaohongshu')
      }
      return map[value] || value
    },
    statusType(s) {
      if (s === 'paid') return 'success'
      if (s === 'failed' || s === 'license_failed') return 'danger'
      return 'info'
    },
    statusText(s) {
      const map = {
        pending: this.$t('gamesPurchase.stPending'),
        paid: this.$t('gamesPurchase.stPaid'),
        failed: this.$t('gamesPurchase.stFailed'),
        processing: this.$t('gamesPurchase.stProcessing'),
        license_failed: this.$t('gamesPurchase.stLicenseFailed'),
        closed: this.$t('gamesPurchase.stClosed')
      }
      return map[s] || s
    }
  }
}
</script>

<style scoped>
.page {
  width: 100%;
}

.orders {
  margin-top: 0;
}

.orders__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.orders__search {
  width: min(100%, 360px);
}

.orders__panel {
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0;
  overflow: visible;
}

.orders__body {
  min-height: 200px;
}

.orders__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-item {
  display: flex;
  align-items: stretch;
  gap: 20px;
  padding: 20px 22px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.order-item:hover {
  border-color: rgba(64, 158, 255, 0.28);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.09);
}

.order-item__cover {
  flex: 0 0 200px;
  width: 200px;
  min-height: 130px;
  overflow: hidden;
  border-radius: 12px;
  background: #eaf3ff;
}

.order-item__img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 130px;
  object-fit: cover;
}

.order-item__placeholder {
  display: grid;
  place-items: center;
  width: 100%;
  min-height: 130px;
  color: #94a3b8;
  font-size: 36px;
}

.order-item__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 2px 0;
}

.order-item__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.order-item__titleWrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.order-item__name {
  margin: 0;
  color: #1f2d3d;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.order-item__price {
  color: #337ecc;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.order-item__status {
  flex: 0 0 auto;
}

.order-item__info {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
}

.order-item__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.order-item__label {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.order-item__value {
  color: #334155;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  word-break: break-all;
}

.order-item__value code {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  background: transparent;
}

.order-item__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 4px;
}

.order-item__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.orders__empty {
  padding: 48px 20px;
  border-radius: 12px;
  background: #f7f8f9;
  color: #8a9096;
  text-align: center;
}

.orders__emptyState {
  padding: 32px 16px 40px;
}

.orders__pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.orders__rebindForm {
  margin-top: 8px;
}

.orders__rebindFee {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
}

.orders__rebindRemainValue {
  color: #c2410c;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.orders__paySelect {
  width: 100%;
}

.orders__payGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
}

.orders__payGrid > * {
  min-width: 0;
}

.orders__payOption {
  display: flex;
  align-items: center;
  gap: 11px;
  position: relative;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e7e9eb;
  border-radius: 10px;
  background: #fff;
  color: #526b72;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
}

.orders__payOption > span:nth-child(2) {
  min-width: 0;
}

.orders__payOption.is-active {
  border-color: #2f6bff;
  box-shadow: 0 0 0 3px rgba(47, 107, 255, 0.08);
}

.orders__payMark {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.orders__payMark--ali {
  background: #1677ff;
}

.orders__payMark--wx {
  background: #07c160;
}

.orders__payOption strong {
  display: block;
  color: #202327;
  font-size: 13px;
}

.orders__payOption small {
  display: block;
  margin-top: 2px;
  color: #95a4a8;
  font-size: 8px;
  letter-spacing: 0.08em;
}

.orders__payOption > i {
  position: absolute;
  top: 9px;
  right: 9px;
  color: #2f6bff;
  opacity: 0;
}

.orders__payOption.is-active > i {
  opacity: 1;
}

.orders__rebindOnlineTip {
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.4;
}

.gp__qr {
  text-align: center;
}

.gp__qrImg {
  width: 220px;
  height: 220px;
  border-radius: 8px;
}

.gp__qrTip {
  margin-top: 12px;
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 900px) {
  .order-item__info {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .order-item {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }

  .orders__payGrid {
    grid-template-columns: 1fr;
  }

  .order-item__cover {
    flex: none;
    width: 100%;
    min-height: 0;
    height: 180px;
  }

  .order-item__img {
    min-height: 180px;
  }

  .order-item__placeholder {
    min-height: 180px;
  }

  .order-item__name {
    font-size: 18px;
  }

  .order-item__price {
    font-size: 20px;
  }

  .order-item__info {
    grid-template-columns: 1fr;
    padding: 12px 14px;
  }

  .order-item__footer {
    justify-content: flex-start;
  }

  .order-item__actions {
    justify-content: flex-start;
    width: 100%;
  }
}
</style>
